/**
 * @author Maxime Mustarda <maxime@inarix.com>
 * @file licenseListAsyncActions.ts
 * @desc Created on Mon Apr 28 2025 13:28:19
 * @copyright All rights reserved @ Inarix
 */

import axios from "axios";
import { API } from "../../../config";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../../../configureStore";
import { GroupInfoData } from "../users/userTypes";
import {
  ExtendedLicense,
  LicenseInstance,
  LicenseStatus,
  PackInstance,
  PackStatus,
} from "./licenceTypes";
import { tryCatch } from "../../utils/queryWrapper";

export const fetchLicenses = createAsyncThunk(
  "licenseList/fetchLicenses",
  async (_, store) => {
    const state = store.getState() as RootState;
    const { user } = state.Authentication;
    const { locale } = state.LayoutReducer;

    const params = [
      "$eager=[licenseTemplate, ownerGroup, packs]",
      "$sort[expiryDate]=-1",
    ].join("&");

    try {
      const result = await axios.get<{ data: ExtendedLicense[] }>(
        `${API}/licenses/license-instance?${params}`,
        {
          headers: {
            "Accept-Language": locale,
            Authorization: `Bearer ${user!.token}`,
          },
        },
      );

      return result.data?.data;
    } catch (err) {
      store.rejectWithValue(err);
    }
  },
);

export const fetchAvailPacks = createAsyncThunk(
  "licenseList/fetchPacks",
  async (_, store) => {
    const state = store.getState() as RootState;
    const { user } = state.Authentication;
    const { locale } = state.LayoutReducer;

    const params = [
      "boundToLicenseInstanceId=null",
      `statusConstantId[$ne]=${PackStatus.expired}`,
    ].join("&");

    try {
      const result = await axios.get<{ data: PackInstance[] }>(
        `${API}/licenses/pack-instance?${params}`,
        {
          headers: {
            "Accept-Language": locale,
            Authorization: `Bearer ${user!.token}`,
          },
        },
      );
      store.dispatch(fetchLicenses());
      return result.data?.data;
    } catch (err) {
      store.rejectWithValue(err);
    }
  },
);

export const unlinkLicenseUser = createAsyncThunk(
  "licenseList/unlinkUser",
  async (licenseId: string, store) => {
    const state = store.getState() as RootState;
    const { user } = state.Authentication;
    const { locale } = state.LayoutReducer;

    try {
      const result = await axios.patch<LicenseInstance>(
        `${API}/licenses/license-instance/${licenseId}`,
        {
          ownerGroupId: null,
          statusConstantId: LicenseStatus.unactivated,
        } satisfies Partial<LicenseInstance>,
        {
          headers: {
            "Accept-Language": locale,
            Authorization: `Bearer ${user!.token}`,
          },
        },
      );
      store.dispatch(fetchLicenses());
      return result.data;
    } catch (err) {
      return store.rejectWithValue(err);
    }
  },
);

export const bindLicenseUser = createAsyncThunk(
  "licenseList/bindUser",
  async (data: { userGroupId: number; licenseId: string }, store) => {
    const state = store.getState() as RootState;
    const { user } = state.Authentication;
    const { locale } = state.LayoutReducer;
    const conf = {
      headers: {
        "Accept-Language": locale,
        Authorization: `Bearer ${user!.token}`,
      },
    };

    const patchRes = await tryCatch(
      axios.patch<LicenseInstance>(
        `${API}/licenses/license-instance/${data.licenseId}`,
        {
          ownerGroupId: data.userGroupId,
        } satisfies Partial<LicenseInstance>,
        conf,
      ),
    );
    if (patchRes.error) {
      return store.rejectWithValue(patchRes.error);
    }

    const groupRes = await tryCatch(
      axios.get<GroupInfoData>(
        `${API}/users/organization-group/${data.userGroupId}`,
        conf,
      ),
    );

    if (groupRes.error) {
      return store.rejectWithValue(groupRes.error);
    }

    return {
      ...patchRes.result.data,
      ownerGroup: groupRes.result.data,
    };
  },
);

export const bindPack = createAsyncThunk(
  "licenseList/bindPack",
  async (data: { packId: string; licenseId: string }, store) => {
    const state = store.getState() as RootState;
    const { user } = state.Authentication;
    const { locale } = state.LayoutReducer;

    try {
      const result = await axios.patch<PackInstance>(
        `${API}/licenses/pack-instance/${data.packId}`,
        {
          boundToLicenseInstanceId: data.licenseId,
        } satisfies Partial<PackInstance>,
        {
          headers: {
            "Accept-Language": locale,
            Authorization: `Bearer ${user!.token}`,
          },
        },
      );
      return result.data;
    } catch (err) {
      store.rejectWithValue(err);
    }
  },
);
